import { useEffect } from "react";
import LafayetteHeroSection from "./components/HeroSection";
import LafayetteStickyNav from "./components/StickyNav";
import LafayetteProcessingTimeSection from "./components/ProcessingTimeSection";
import LafayetteServicesSection from "./components/ServicesSection";
import LafayetteLocalAreaSection from "./components/LocalAreaSection";
import LafayetteTravelTimingSection from "./components/TravelTimingSection";
import LafayetteDestinationsSection from "./components/DestinationsSection";
import LafayetteTestimonialsSection from "./components/TestimonialsSection";
import LafayetteAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import LafayetteComparisonSection from "./components/ComparisonSection";
import LafayetteMistakesSection from "./components/MistakesSection";
import LafayetteHowItWorksSection from "./components/HowItWorksSection";
import LafayetteFAQSection, { faqs } from "./components/FAQSection";
import LafayetteHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import LafayetteFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function LafayettePage() {
  useEffect(() => {
    document.title = "Emergency Passport Lafayette, LA — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/lafayette/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Lafayette, LA — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Acadiana oilfield engineers, ULL students, Cajun heritage travelers, and families booking New Orleans cruises.");
    setMeta("geo.placename", "Lafayette, Louisiana");
    setMeta("geo.region", "US-LA");
    setMeta("geo.position", "30.2241;-92.0198");
    setMeta("ICBM", "30.2241, -92.0198");

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Louisiana", item: "https://fastpassportcenter.com/louisiana/" },
          { "@type": "ListItem", position: 3, name: "Lafayette Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Emergency and expedited passport services for Lafayette, LA — as fast as 24 hours. Registered U.S. Department of State courier serving Acadiana.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "Lafayette", containedInPlace: { "@type": "State", name: "Louisiana" } },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data-lafayette";
    document.getElementById("structured-data-lafayette")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      document.getElementById("structured-data-lafayette")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <LafayetteHeroSection />
      <LafayetteStickyNav />
      <LafayetteProcessingTimeSection />
      <LafayetteServicesSection />
      <LafayetteLocalAreaSection />
      <LafayetteTravelTimingSection />
      <LafayetteDestinationsSection />
      <LafayetteTestimonialsSection />
      <LafayetteAcceptanceFacilitiesSection />
      <LafayetteComparisonSection />
      <LafayetteMistakesSection />
      <LafayetteHowItWorksSection />
      <LafayetteFAQSection />
      <LafayetteHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/lafayette" state="LA" />
      <LafayetteFinalCTA />
    </div>
  );
}